import React, { useState, useEffect, useCallback } from 'react';

const DiceElement = ({
  element,
  isEditor = false,
  canInteract = true,
  onRoll,
  onUpdateContent,
  syncedValue = null
}) => {
  const { content } = element;
  const { diceType = 'd6', color = '#1e1e1e', dotColor = '#ffffff' } = content;

  const [currentValue, setCurrentValue] = useState(content.currentValue || 1);
  const [isRolling, setIsRolling] = useState(false);

  // Sync value from external source
  useEffect(() => {
    if (syncedValue !== null && !isRolling) {
      setCurrentValue(syncedValue);
    }
  }, [syncedValue, isRolling]);

  const getMaxValue = (type) => {
    const dice = {
      'd4': 4,
      'd6': 6,
      'd8': 8,
      'd10': 10,
      'd12': 12,
      'd20': 20
    };
    return dice[type] || 6;
  };

  const handleRoll = useCallback(() => {
    if (isRolling || isEditor || !canInteract) return;

    setIsRolling(true);

    // Animation: rapidly change values
    const maxValue = getMaxValue(diceType);
    const animationDuration = 1000;
    const intervalTime = 80;
    const iterations = animationDuration / intervalTime;

    let count = 0;
    const interval = setInterval(() => {
      setCurrentValue(Math.floor(Math.random() * maxValue) + 1);
      count++;

      if (count >= iterations) {
        clearInterval(interval);
        const finalValue = Math.floor(Math.random() * maxValue) + 1;
        setCurrentValue(finalValue);
        setIsRolling(false);

        if (onRoll) {
          onRoll(finalValue);
        }

        if (onUpdateContent) {
          onUpdateContent({ ...content, currentValue: finalValue });
        }
      }
    }, intervalTime);
  }, [isRolling, isEditor, canInteract, diceType, content, onRoll, onUpdateContent]);

  // Render dots for d6
  const renderD6Dots = (value) => {
    const dotSize = '18%';
    const dotPositions = {
      1: [{ top: '50%', left: '50%' }],
      2: [{ top: '25%', left: '25%' }, { top: '75%', left: '75%' }],
      3: [{ top: '25%', left: '25%' }, { top: '50%', left: '50%' }, { top: '75%', left: '75%' }],
      4: [{ top: '25%', left: '25%' }, { top: '25%', left: '75%' }, { top: '75%', left: '25%' }, { top: '75%', left: '75%' }],
      5: [{ top: '25%', left: '25%' }, { top: '25%', left: '75%' }, { top: '50%', left: '50%' }, { top: '75%', left: '25%' }, { top: '75%', left: '75%' }],
      6: [{ top: '25%', left: '25%' }, { top: '25%', left: '75%' }, { top: '50%', left: '25%' }, { top: '50%', left: '75%' }, { top: '75%', left: '25%' }, { top: '75%', left: '75%' }]
    };

    const dots = dotPositions[value] || dotPositions[1];

    return dots.map((pos, index) => (
      <div
        key={index}
        className="absolute rounded-full"
        style={{
          width: dotSize,
          height: dotSize,
          backgroundColor: dotColor,
          top: pos.top,
          left: pos.left,
          transform: 'translate(-50%, -50%)',
          boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.2)'
        }}
      />
    ));
  };

  // Render number for other dice types
  const renderNumber = (value) => {
    return (
      <span
        className="font-bold"
        style={{
          color: dotColor,
          fontSize: 'clamp(16px, 40%, 48px)',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}
      >
        {value}
      </span>
    );
  };

  const isD6 = diceType === 'd6';

  return (
    <div
      className="w-full h-full flex items-center justify-center"
      onClick={handleRoll}
      style={{ cursor: canInteract && !isEditor ? 'pointer' : 'default' }}
    >
      <div
        className={`relative flex items-center justify-center ${isD6 ? 'rounded-xl' : 'rounded-full'}`}
        style={{
          width: '90%',
          height: '90%',
          maxWidth: '100%',
          maxHeight: '100%',
          aspectRatio: '1',
          backgroundColor: color,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.1)',
          transform: isRolling ? 'scale(0.95)' : 'scale(1)',
          transition: 'transform 0.1s ease-in-out',
          animation: isRolling ? 'shake 0.1s infinite' : 'none'
        }}
      >
        {isD6 ? renderD6Dots(currentValue) : renderNumber(currentValue)}
      </div>

      {/* Editor overlay */}
      {isEditor && (
        <div className="absolute inset-0 flex items-end justify-center pb-1 pointer-events-none">
          <div className="bg-black/50 text-white text-xs px-2 py-0.5 rounded">
            {diceType.toUpperCase()}
          </div>
        </div>
      )}

      {/* Shake animation keyframes */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: rotate(0deg) scale(0.95); }
          25% { transform: rotate(-5deg) scale(0.95); }
          75% { transform: rotate(5deg) scale(0.95); }
        }
      `}</style>
    </div>
  );
};

export default DiceElement;
